/**
 * GovernedAI — the ONE browser transport for AI calls that carry client data.
 *
 * Every prompt with client NPI in it (names, DOBs, license numbers, VINs,
 * addresses, policy detail, drafts about a client) rides POST
 * /api/insurance-agent: routing pinned SERVER-side to the agency's
 * zero-retention key, hash-chained withAiGovernance audit, and the `agent`
 * daily quota. Never AIProvider.ask/chat — their Google/OpenAI/OpenRouter
 * paths fetch the provider directly from the browser with the user's own
 * consumer key, which is exactly the leak the 2026-09 NPI audit closed.
 * tests/governed-transport.test.js pins the allowlist of remaining
 * AIProvider callers (non-client data only) so the class cannot re-appear.
 *
 * The endpoint's caps are enforced HERE first — breaching them 400s the
 * whole turn instead of degrading it: 24k chars per message, ≤200k per
 * conversation (we budget 180k), a bounded message window. The newest
 * message always survives. Same numbers and reasoning as the Intake Assist
 * `_governedChat` this generalizes (js/intake-assist.js).
 *
 * API (AIProvider-compatible return shape, so migrated call sites keep
 * reading `result.text`):
 *   GovernedAI.ask(system, user, opts?)      → Promise<{text}>
 *   GovernedAI.chat(system, history, opts?)  → Promise<{text}>  history = [{role, content}]
 *   opts: { responseFormat: 'json', maxTokens } (optional)
 *   GovernedAI.isAvailable()          → boolean (signed-in check)
 */
window.GovernedAI = (() => {
    'use strict';

    const CHAT_WINDOW = 40;
    const MSG_CHAR_CAP = 24000;      // endpoint MAX_CONTENT_CHARS
    const TOTAL_CHAR_BUDGET = 180000; // headroom under the endpoint's 200k total

    function isAvailable() {
        return !!(window.Auth && typeof Auth.apiFetch === 'function');
    }

    /** Bound a history to the endpoint's caps: window, per-message, total. */
    function _boundHistory(history) {
        const recent = (Array.isArray(history) ? history : [])
            .filter((m) => m && (m.role === 'user' || m.role === 'assistant')
                && typeof m.content === 'string' && m.content)
            .slice(-CHAT_WINDOW)
            // A pasted wall of text should truncate visibly, not 400 the turn.
            .map((m) => ({ role: m.role, content: String(m.content).slice(0, MSG_CHAR_CAP) }));
        // Newest-first accumulation against the TOTAL cap. The newest message
        // always survives (it is ≤ MSG_CHAR_CAP < budget).
        const messages = [];
        let total = 0;
        for (let i = recent.length - 1; i >= 0; i--) {
            total += recent[i].content.length;
            if (total > TOTAL_CHAR_BUDGET && messages.length) break;
            messages.unshift(recent[i]);
        }
        return messages;
    }

    // opts (optional): { responseFormat: 'json', maxTokens } — honoured by the
    // endpoint's plain text path; anything else is ignored.
    function _extras(opts) {
        const o = opts || {};
        const out = {};
        if (o.responseFormat === 'json') out.responseFormat = 'json';
        if (Number.isFinite(Number(o.maxTokens)) && Number(o.maxTokens) > 0) out.maxTokens = Math.floor(Number(o.maxTokens));
        return out;
    }

    async function chat(system, history, opts) {
        if (!isAvailable()) {
            throw new Error('Sign in to use smart features. They run on the agency\'s secure route.');
        }
        const messages = _boundHistory(history);
        if (!messages.length) throw new Error('Nothing to send.');
        const res = await Auth.apiFetch('/api/insurance-agent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(Object.assign(
                { system: String(system || '').slice(0, MSG_CHAR_CAP), messages }, _extras(opts))),
        });
        let json = null;
        try { json = await res.json(); } catch (_) { /* non-JSON */ }
        if (!res.ok) {
            // The A-055 rule, kept for every migrated caller: a quota 429 says
            // so AND says when it resets — never "check your settings" for an
            // outage, and no status is ever silent.
            let msg = (json && (json.error || json.message)) || `Request failed (${res.status})`;
            if (res.status === 429 && json && json.resetsAt) {
                const when = new Date(json.resetsAt);
                if (Number.isFinite(when.getTime())) {
                    msg += ` Resets ${when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`;
                }
            }
            throw new Error(msg);
        }
        return { text: (json && json.text) || '' };
    }

    function ask(system, user, opts) {
        return chat(system, [{ role: 'user', content: String(user == null ? '' : user) }], opts);
    }

    return { ask, chat, isAvailable };
})();
